import { useMediaQuery } from '@material-ui/core'
import Image from 'next/legacy/image'
import { useRouter } from 'next/router'
import React, { PropsWithChildren, useMemo } from 'react'
import styled, { useTheme } from 'styled-components'
import stickyDesktop from './images/sticky-desktop.svg'
import regularUpdatedMobile from './images/cusa_logo_mobile_2025.svg'
import regularUpdatedDesktop from './images/cusa_logo_2025.svg'

export type CreditUnionLogoProps = {
  notext?: boolean
}

export const StyledLogoContainer = styled.figure<{ $width: number; $height: number }>`
  margin: 0;
  display: flex;
  position: relative;
  width: ${props => props.theme.typography.pxToRem(props.$width)};
  height: ${props => props.theme.typography.pxToRem(props.$height)};

  ${props => props.theme.breakpoints.down('xs')} {
    span {
        width: 100% !important;
    }    
  }
`

type LogoVariant = 'home-desktop' | 'regular-desktop' | 'regular-tablet' | 'regular-mobile' | 'sticky-desktop' | 'sticky-tablet' | 'sticky-mobile' | 'regular-updated-mobile' | 'regular-updated-desktop'

const logoMap: Record<LogoVariant, string> = {
  'home-desktop': regularUpdatedDesktop,
  'regular-desktop': regularUpdatedDesktop,
  'regular-tablet': regularUpdatedDesktop,
  'regular-mobile': regularUpdatedMobile,
  'sticky-desktop': stickyDesktop,
  'sticky-tablet': stickyDesktop,
  'sticky-mobile': stickyDesktop,
  'regular-updated-mobile': regularUpdatedMobile,
  'regular-updated-desktop': regularUpdatedDesktop,
}
const sizeMap: Record<LogoVariant, [number, number]> = {
  'home-desktop': [280, 131],
  'regular-desktop': [280, 131],
  'regular-tablet': [230, 105],
  'regular-mobile': [155, 83],
  'sticky-desktop': [102, 50],
  'sticky-tablet': [57, 43],
  'sticky-mobile': [57, 43],
  'regular-updated-desktop': [280, 131],
  'regular-updated-mobile': [155, 83],
}

export const CreditUnionLogo: React.FC<PropsWithChildren<CreditUnionLogoProps>> = ({ notext, ...props }) => {
  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up('xl'))
  const isTablet = useMediaQuery(theme.breakpoints.up('md'))
  const isMobile = useMediaQuery(theme.breakpoints.up('sm'))
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const router = useRouter()

  const variant: LogoVariant = useMemo(() => {

    if (isSmallMobile) {
      return notext ? 'sticky-mobile' : 'regular-mobile'
    } else if (isMobile && !isTablet && !isDesktop) {
      return notext ? 'sticky-mobile' : 'regular-mobile'
    } else if (isTablet && !isDesktop) {
      return notext ? 'sticky-desktop' : 'regular-tablet'
    } else if (isDesktop && router.route === '/') {
      return notext ? 'sticky-desktop' : 'home-desktop'
    } else if (isDesktop) {
      return notext ? 'sticky-desktop' : 'regular-desktop'
    } else {
      return notext ? 'sticky-mobile' : 'regular-mobile'
    }
  }, [notext, isDesktop, isTablet, isMobile, isSmallMobile])

  const src = useMemo(() => logoMap[variant], [variant])
  const [width, height] = useMemo(() => sizeMap[variant], [variant])

  return (
    <StyledLogoContainer $width={width} $height={height}>
      <Image priority src={src} width={width} height={height} {...props} alt={'logo'} />
    </StyledLogoContainer>
  )
}

export const StyledCreditUnionLogo = styled(CreditUnionLogo)`
  transition: all 0.2s ease;
`
